import type { Node } from '@xyflow/react';

export type OrgMvpNodeKind = 'person' | 'department';

export type OrgMvpFigureKey =
  | 'person'
  | 'woman'
  | 'team'
  | 'manager'
  | 'operator'
  | 'executive'
  | 'user_add'
  | 'user_remove'
  | 'user_block'
  | 'hr'
  | 'health'
  | 'security'
  | 'maintenance'
  | 'construction_worker'
  | 'admin'
  | 'finance'
  | 'bank'
  | 'sales'
  | 'marketing'
  | 'support'
  | 'legal'
  | 'logistics_ship'
  | 'logistics_plane'
  | 'agro'
  | 'sustainability'
  | 'recycle'
  | 'it'
  | 'data'
  | 'dev'
  | 'network'
  | 'design'
  | 'design_tool'
  | 'media'
  | 'music'
  | 'food'
  | 'coffee'
  | 'innovation'
  | 'growth'
  | 'startup'
  | 'goal'
  | 'partnership'
  | 'award'
  | 'star'
  | 'flag'
  | 'location'
  | 'global'
  | 'phone'
  | 'document'
  | 'currency'
  | 'card'
  | 'puzzle'
  | 'settings'
  | 'tools'
  | 'anchor'
  | 'home'
  | 'forklift'
  | 'warehouse'
  | 'truck'
  | 'factory'
  | 'construction'
  | 'package'
  | 'boxes'
  | 'container'
  | 'briefcase'
  | 'building'
  | 'building_alt';

export type OrgMvpFigure =
  | { type: 'catalog'; key: OrgMvpFigureKey }
  // storagePath/bucket só existem em figuras enviadas pelo upload do canvas — a `url`
  // é presigned e expira, então é o par storagePath+bucket que persiste no banco.
  | { type: 'upload'; url: string; name: string; storagePath?: string; bucket?: string; mimeType?: string };

export type OrgMvpCardQuickView = {
  nome: string;
  cargo?: string | null;
  departamento?: string | null;
  email?: string | null;
};

export type OrgMvpCardData = {
  kind: OrgMvpNodeKind;
  label: string;
  subtitle: string;
  description?: string;
  sourceType?: 'SYSTEM_USER' | 'ORG_ONLY';
  usuarioId?: string | null;
  departamentoId?: string | null;
  cronogramaId?: string | null;
  cronogramaTitulo?: string | null;
  /** Card de usuário com "Mostrar foto do usuário" desligado (persiste em `data_json.ocultarFoto`). */
  ocultarFoto?: boolean;
  color: string;
  textColor: string;
  figure: OrgMvpFigure;
  onChange?: (nodeId: string, updates: Partial<OrgMvpCardData>) => void;
  /** Avisa que a edição inline do nome terminou, para o clique que a encerrou não fechar o painel */
  onEditEnd?: (nodeId: string) => void;
  /** Clique no ícone do card: sincroniza o catálogo de figuras da barra lateral com este card */
  onFigureClick?: (nodeId: string) => void;
  readonly?: boolean;
  // Campos derivados, injetados apenas na camada de exibição (nunca persistem)
  directReports?: number;
  hasChildren?: boolean;
  isCollapsed?: boolean;
  collapsedCount?: number;
  onToggleCollapse?: (nodeId: string) => void;
  quickView?: OrgMvpCardQuickView;
  highlighted?: boolean;
  dimmed?: boolean;
  [key: string]: unknown;
};

export type OrgMvpFigureData = {
  figure: OrgMvpFigure;
  color: string;
  label: string;
  showBackground: boolean;
  onChange?: (nodeId: string, updates: Partial<OrgMvpFigureData>) => void;
  readonly?: boolean;
  [key: string]: unknown;
};

/** Onde o nome da coluna é desenhado. Uma posição só — nunca duas ao mesmo tempo. */
export type OrgMvpColumnTitlePosition = 'left' | 'top' | 'bottom';

export type OrgMvpColumnData = {
  title: string;
  color: string;
  orientation: 'vertical' | 'horizontal';
  titlePosition?: OrgMvpColumnTitlePosition;
  onChange?: (nodeId: string, updates: Partial<OrgMvpColumnData>) => void;
  /** Avisa que a edição inline do título terminou, para o clique que a encerrou não fechar o painel */
  onEditEnd?: (nodeId: string) => void;
  readonly?: boolean;
  [key: string]: unknown;
};

/** Colunas salvas antes da opção de posição não têm `titlePosition`; a orientação
 *  antiga define o padrão (horizontal desenhava o título girado à esquerda). */
export function resolveColumnTitlePosition(
  column: Pick<OrgMvpColumnData, 'titlePosition' | 'orientation'>,
): OrgMvpColumnTitlePosition {
  if (column.titlePosition === 'left' || column.titlePosition === 'top' || column.titlePosition === 'bottom') {
    return column.titlePosition;
  }
  return column.orientation === 'horizontal' ? 'left' : 'top';
}

export type OrgMvpTextData = {
  text: string;
  color: string;
  fontSize: number;
  onChange?: (nodeId: string, updates: Partial<OrgMvpTextData>) => void;
  readonly?: boolean;
  [key: string]: unknown;
};

export type OrgMvpAnnotationData = {
  text: string;
  color: string;
  onChange?: (nodeId: string, updates: Partial<OrgMvpAnnotationData>) => void;
  readonly?: boolean;
  [key: string]: unknown;
};

export type OrgMvpTableData = {
  rows: string[][];
  onChange?: (nodeId: string, updates: Partial<OrgMvpTableData>) => void;
  readonly?: boolean;
  [key: string]: unknown;
};

export type OrgMvpShapeKind = 'square' | 'circle' | 'line';

/** Forma livre de desenho: elemento solto no canvas, sem vínculo com pessoa/departamento.
 *  `dashed` é estilo de traço da forma "linha", não uma forma separada. */
export type OrgMvpShapeData = {
  shape?: OrgMvpShapeKind;
  dashed?: boolean;
  color: string;
  fill?: string;
  strokeWidth?: number;
  onChange?: (nodeId: string, updates: Partial<OrgMvpShapeData>) => void;
  readonly?: boolean;
  [key: string]: unknown;
};

export type OrgMvpNode = Node<
  | OrgMvpCardData
  | OrgMvpColumnData
  | OrgMvpFigureData
  | OrgMvpTextData
  | OrgMvpAnnotationData
  | OrgMvpTableData
  | OrgMvpShapeData
>;

export type OrgMvpApiNode = {
  id: string;
  node_type: 'card' | 'column' | 'figure' | 'text' | 'table' | 'annotation' | 'shape';
  card_kind?: 'person' | 'department' | null;
  source_type?: 'SYSTEM_USER' | 'ORG_ONLY' | null;
  usuario_id?: string | null;
  departamento_id?: string | null;
  cronograma_id?: string | null;
  cronograma?: {
    id: string;
    titulo: string;
    status?: string | null;
  } | null;
  usuario?: {
    id: string;
    nome: string;
    email?: string | null;
  } | null;
  departamento?: {
    id: string;
    nome: string;
  } | null;
  label?: string | null;
  subtitle?: string | null;
  description?: string | null;
  color?: string | null;
  text_color?: string | null;
  figure_type?: 'catalog' | 'upload' | null;
  figure_key?: string | null;
  figure_storage_path?: string | null;
  figure_bucket_name?: string | null;
  figure_mime_type?: string | null;
  figure_url?: string | null;
  avatar_url?: string | null;
  show_background?: boolean | null;
  orientation?: 'vertical' | 'horizontal' | null;
  position_x?: number | null;
  position_y?: number | null;
  width?: number | null;
  height?: number | null;
  z_index?: number | null;
  data_json?: Record<string, unknown> | null;
};

export type OrgMvpApiEdge = {
  id: string;
  source_node_id: string;
  target_node_id: string;
  source_handle?: string | null;
  target_handle?: string | null;
  edge_type?: string | null;
  tipo?: string | null;
  descricao?: string | null;
  animated?: boolean | null;
  stroke?: string | null;
  stroke_width?: number | null;
  data_json?: Record<string, unknown> | null;
};
